/**
 * The one navbar of a workspace (I-3641, `shell.tiles.navbar`): inside a tile,
 * the app's menu goes up to the page as plain data; the page's navbar shows the
 * active tile's menu and sends a click back, so the entry runs in its own tile.
 */
import {
  computed,
  defineComponent,
  h,
  onBeforeUnmount,
  ref,
  render,
  watch,
  type Component,
  type ComputedRef,
} from "vue";
import type { Router } from "vue-router";
import {
  reportConnectTile,
  useAccountConfig,
  type ConnectTileCommand,
  type ConnectTileNav,
  type ConnectTileNavItem,
  type ConnectTileNavbarMode,
} from "@blpde/connect";
import type { NavItem } from "@/integration/nav";
import { activateNavItem } from "@/integration/navAction";

/** The setting, for the page and (until the page says otherwise) a tile. */
export function useTileNavbarMode(): ComputedRef<ConnectTileNavbarMode> {
  const config = useAccountConfig<{
    tiles?: { navbar?: ConnectTileNavbarMode };
  }>("shell");
  return computed(() =>
    config.effective.value?.tiles?.navbar === "tile" ? "tile" : "shared",
  );
}

/** Tabs and tiles at all (`shell.tiles.enabled`); off, Connect is one page per browser tab. */
export function useTilesEnabled(): ComputedRef<boolean> {
  const config = useAccountConfig<{ tiles?: { enabled?: boolean } }>("shell");
  return computed(() => config.effective.value?.tiles?.enabled !== false);
}

/** „Daneben“ uses a tile that is already there (`shell.tiles.split_target`), or always adds one. */
export function tileSplitReuses(): boolean {
  const config = useAccountConfig<{ tiles?: { split_target?: string } }>(
    "shell",
    { immediate: false },
  );
  return config.effective.value?.tiles?.split_target !== "new";
}

const iconMarkup = new WeakMap<object, string>();

/** A component does not cross windows, its rendered SVG does. */
export function iconToMarkup(icon: Component | undefined): string | undefined {
  if (!icon) return undefined;
  const known = iconMarkup.get(icon);
  if (known !== undefined) return known;
  const host = document.createElement("div");
  render(h(icon), host);
  const markup = host.innerHTML;
  render(null, host);
  iconMarkup.set(icon, markup);
  return markup;
}

export function toTileNavItems(
  items: readonly NavItem[],
  label: (text: string) => string,
): ConnectTileNavItem[] {
  // A panel is a live component in the tile; it has no form that could cross.
  return items
    .filter((item) => !item.panel)
    .map((item) => ({
      id: item.id,
      text: label(item.text),
      icon: iconToMarkup(item.icon),
      iconOnly: item.iconOnly,
      disabled: item.disabled,
      badge: item.badge,
      children: item.children
        ? toTileNavItems(item.children, label)
        : undefined,
    }));
}

export function findNavItem(
  items: readonly NavItem[],
  id: string,
): NavItem | undefined {
  for (const item of items) {
    if (item.id === id) return item;
    const below = item.children && findNavItem(item.children, id);
    if (below) return below;
  }
  return undefined;
}

/** A tile: report the menu and run what the page sends back. Returns the current mode. */
export function useTileNavReport(options: {
  left: () => readonly NavItem[];
  right: () => readonly NavItem[];
  label: (text: string) => string;
  router: Router;
}): ComputedRef<ConnectTileNavbarMode> {
  const stored = useTileNavbarMode();
  const sent = ref<ConnectTileNavbarMode | null>(null);
  const mode = computed(() => sent.value ?? stored.value);

  watch(
    () => ({
      left: toTileNavItems(options.left(), options.label),
      right: toTileNavItems(options.right(), options.label),
    }),
    (nav: ConnectTileNav) => reportConnectTile({ nav }),
    { immediate: true },
  );

  function onMessage(event: MessageEvent<ConnectTileCommand>): void {
    if (event.source !== window.parent || event.origin !== location.origin)
      return;
    if (event.data?.type === "connect.tile.navbar")
      sent.value = event.data.mode;
    if (event.data?.type !== "connect.tile.nav-action") return;
    const item = findNavItem(
      [...options.left(), ...options.right()],
      event.data.itemId,
    );
    if (item) activateNavItem(item, options.router);
  }
  window.addEventListener("message", onMessage);
  onBeforeUnmount(() => window.removeEventListener("message", onMessage));
  return mode;
}

const svgIcon = (markup: string): Component =>
  defineComponent({
    name: "TileNavIcon",
    setup: () => () =>
      h("span", { class: "cx-tile-nav-icon", innerHTML: markup }),
  });
const svgIcons = new Map<string, Component>();

/** The page: a tile's reported menu as navbar entries that act in that tile. */
export function fromTileNavItems(
  items: readonly ConnectTileNavItem[],
  run: (itemId: string) => void,
): NavItem[] {
  return items.map((item) => {
    let icon: Component | undefined;
    if (item.icon) {
      icon = svgIcons.get(item.icon) ?? svgIcon(item.icon);
      svgIcons.set(item.icon, icon);
    }
    const children = item.children?.length
      ? fromTileNavItems(item.children, run)
      : undefined;
    return {
      id: item.id,
      text: item.text,
      icon,
      iconOnly: item.iconOnly,
      disabled: item.disabled,
      badge: item.badge,
      children,
      action: children ? undefined : () => run(item.id),
    };
  });
}
